<template>
	<div>
		<div
			class="list-tile"
			:class="{'is-mobile': isMobileWebApp}"
			role="region"
			data-testid="video-tile"
			:aria-labelledby="itemId"
			@click="handleClick"
		>
			<div class="list-tile-container">
				<div
					v-if="!isTaggingToolMode"
					class="shadow -desktop-hover-only"
				/>
				<div class="list-tile--leading">
					<div
						v-if="!imageLoadingError"
						class="list-tile--image"
						:style="{
							backgroundImage: `url(${imageUrl})`,
						}"
					>
						<div class="play-button">
							<Icon
								icon-name="button-play-filled"
								size="sm"
							/>
						</div>
						<img
							style="display: none;"
							:src="imageUrl"
							@error="onImageLoadingError"
						>
					</div>
					<div
						v-if="imageLoadingError"
						class="list-tile--image has-error"
					>
						<div class="error-bg" />
						<span>{{ previewNotFoundLabel }}</span>
					</div>
					<div class="list-tile--leading-bg">
						<BadgeLive
							v-if="item.isLive"
						/>
						<time
							v-else
							class="duration"
						>{{ item.duration }}</time>
					</div>
				</div>
				<div class="list-tile--middle">
					<div class="list-tile--content centered">
						<div
							class="list-tile--description"
						>
							<span>{{ description }}</span>
						</div>
						<LabelWithTooltip
							class="top"
							ref-element="title"
							:label="title"
						>
							<h3
								ref="title"
								class="list-tile--title"
							>
								{{ title }}
							</h3>
						</LabelWithTooltip>
					</div>
				</div>
				<div class="list-tile--trailing">
					<Icon
						class="share-icon -desktop-hover-only"
						icon-name="network-communication"
						size="lg"
						data-testid="share-video-button"
						@click.stop="handleShare()"
					/>
				</div>
			</div>
			<div class="divider" />
		</div>
	</div>
</template>

<script>
import BadgeLive from '@shared/components/BadgeLive';
import { mapGetters } from 'vuex';

export default {
	name:       'VideoTile',
	components: {
		BadgeLive,
	},
	props: {
		item: {
			type:    Object,
			default: null,
		},
		previewNotFoundLabel: {
			type:    String,
			default: 'Preview not found',
		},
		sportName: {
			type:    String,
			default: '',
		},
		menuItems: {
			type:    Array,
			default: () => [],
		},
		title: {
			type: String,
		},
		description: {
			type: String,
		},
	},
	data() {
		return {
			imageLoadingError: false,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:    'app/isMobileWebApp',
			isTaggingToolMode: 'app/isTaggingToolMode',
		}),
		itemId() {
			return this.item.id;
		},
		imageUrl() {
			if (!this.item) {
				return null;
			}
			if (!this.item.imageUrl) {
				return '/images/background-player.jpg';
			}

			return this.$Helpers
				? this.$Helpers.getScaledImageUrl(this.item.imageUrl, 180)
				: this.item.imageUrl;
		},
	},
	methods: {
		handleShare() {
			this.$emit('share');
		},
		handleClick() {
			this.$emit('click', this.item);
		},
		onImageLoadingError() {
			this.imageLoadingError = true;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.list-tile--content {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.list-tile--description {
	color: var(--color-grey-300);
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	font-size: 12px;
	line-height: 16px;
	margin-top: 2px;
	margin-bottom: 4px;
}

.divider {
	height: 1px;
	margin-left: 8px;
	margin-right: 8px;
	margin-top: 8px;
	background: var(--color-grey-900);
}

.list-tile {
	display: flex;
	flex-direction: column;

	.list-tile-container {
		position: relative;
		padding: 8px 8px 0 8px;
		display: flex;
	}

	.list-tile--title {
		@include text-sm-2;
		font-weight: 500;
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box; // stylelint-disable-line value-no-vendor-prefix
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical; // stylelint-disable-line property-no-vendor-prefix
		white-space: normal
	}

	@media(hover:hover) {
		&:hover {
			.share-icon.-desktop-hover-only {
				visibility: visible;
				opacity: 1;
			}
			.shadow.-desktop-hover-only {
				visibility: visible;
				opacity: 1;
			}

			.list-tile--image {
				.play-button {
					display: flex;
				}
			}
			.list-tile--leading .list-tile--leading-bg {
				display: none;
			}
		}

		&:hover,
		&.locked {
			background: var(--color-grey-950);
			cursor: pointer;
		}

	}

	&:not(.is-mobile) {
		.-desktop-hover-only {
			visibility: hidden;
			opacity: 0;
			transition: opacity 0.3s ease;
		}

		.shadow {
			position: absolute;
			right: 0;
			top: 0;
			width: 120px;
			height: 81px;
			background: linear-gradient(90deg, rgba(var(--color-rgb-grey-950), 0) 0%, var(--color-grey-950) 50%);
		}
	}

	.list-tile--leading {
		max-width: 112px;
		position: relative;

		.list-tile--leading-bg {
			position: absolute;
			display: flex;
			align-items: center;
			justify-content: flex-end;
			width: 100%;
			right: 0;
			margin-right: 4px;
			margin-bottom: 4px;
			text-align: center;
			bottom: 0;

			.duration {
				@include text-sm-1;
				padding: 2px;
				background: rgba(var(--color-rgb-black), 0.6);
				border-radius: 2px;

				body.-t-light & { // stylelint-disable-line selector-no-qualifying-type
					background: rgba(var(--color-rgb-white), 0.6);
				}
			}
		}

		img {
			width: 100%;
		}
	}

	.list-tile--image {
		width: 112px;
		height: 63px;
		background-size: cover;
		background-position: center;
		border-radius: 2px;

		&.has-error {
			background: var(--color-soft-white);

			.error-bg {
				height: 45px;
				background-position-x: 50%;
			}

			span {
				position: relative;
				top: -2px;
				display: block;
				text-align: center;
				font-size: 13px;
				color: var(--color-grey-700);
			}
		}
	}

	.list-tile--middle {
		flex-grow: 1;
		min-width: 0;
		padding: 0 8px 0 16px;
	}

	.list-tile--trailing {
		position: relative;
		width: 23px;
		display: flex;
		justify-content: center;
		align-items: center;
	}
}

.top {
	display: flex;
	align-items: center;
}

.live-badge {
	margin-bottom: 0
}

.list-tile {
	.play-button {
		justify-content: center;
		align-items: center;
		background: var(--color-black);
		opacity: 0.8;
		height: 32px;
		width: 32px;
		position: absolute;
		margin: auto auto auto auto;
		left: 0;
		top: 0;
		bottom: 0;
		right: 0;
		text-align: center;
		display: none;
		border-radius: 50%;

		body.-t-light & { // stylelint-disable-line selector-no-qualifying-type
			background: var(--color-white);
		}
	}
}
</style>
